/**
 * SectorSpecialtiesPage — Gestion des spécialités par secteur
 * CRUD complet via REST /api/sector-specialties
 */

import { useState, useEffect, useCallback } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Switch,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import WorkIcon from '@mui/icons-material/Work';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.e-kaa.app';
const TOKEN_KEY = 'ekaa_admin_token';

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface BusinessSector {
  id: string;
  label: string;
  emoji: string | null;
  isActive: boolean;
  displayOrder: number;
}

interface SectorSpecialty {
  id: string;
  label: string;
  value: string;
  emoji: string | null;
  isActive: boolean;
  displayOrder: number;
  sectorId: string;
  sector: BusinessSector;
  createdAt: string;
  updatedAt: string;
}

type SpecialtyDraft = Partial<
  Pick<SectorSpecialty, 'label' | 'value' | 'emoji' | 'isActive' | 'displayOrder'>
>;

export function SectorSpecialtiesPage() {
  const notify = useNotify();

  const [specialties, setSpecialties] = useState<SectorSpecialty[]>([]);
  const [sectors, setSectors] = useState<BusinessSector[]>([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState<Record<string, SpecialtyDraft>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});

  // New specialty form
  const [newSectorId, setNewSectorId] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newEmoji, setNewEmoji] = useState('');
  const [newOrder, setNewOrder] = useState('');
  const [creating, setCreating] = useState(false);

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState<SectorSpecialty | null>(null);

  // Filter by sector
  const [selectedSectorFilter, setSelectedSectorFilter] = useState<string>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch sectors
      const sectorsRes = await fetch(`${API_URL}/api/sectors/all`, {
        headers: authHeaders(),
      });
      if (sectorsRes.ok) {
        const sectorsData = await sectorsRes.json();
        if (Array.isArray(sectorsData)) setSectors(sectorsData);
      }

      // Fetch specialties
      const specialtiesRes = await fetch(`${API_URL}/api/sector-specialties`, {
        headers: authHeaders(),
      });
      if (!specialtiesRes.ok) throw new Error(`HTTP ${specialtiesRes.status}`);
      const specialtiesData = await specialtiesRes.json();
      if (Array.isArray(specialtiesData)) setSpecialties(specialtiesData);
    } catch (err: any) {
      notify(`Erreur de chargement: ${err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Field helpers ──────────────────────────────────────────────────────────

  function getField<K extends keyof SectorSpecialty>(
    specialty: SectorSpecialty,
    key: K,
  ): SectorSpecialty[K] {
    return (edits[specialty.id]?.[key as keyof SpecialtyDraft] as SectorSpecialty[K]) ??
      specialty[key];
  }

  function setField(id: string, field: keyof SpecialtyDraft, value: unknown) {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  function isDirty(id: string) {
    return !!edits[id] && Object.keys(edits[id]).length > 0;
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave(specialty: SectorSpecialty) {
    if (!edits[specialty.id]) return;
    setSaving((p) => ({ ...p, [specialty.id]: true }));
    try {
      const res = await fetch(`${API_URL}/api/sector-specialties/${specialty.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(edits[specialty.id]),
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.message || `HTTP ${res.status}`);
      setSpecialties((prev) =>
        prev.map((s) => (s.id === specialty.id ? { ...updated, sector: specialty.sector } : s)),
      );
      setEdits((prev) => {
        const c = { ...prev };
        delete c[specialty.id];
        return c;
      });
      notify('Spécialité mise à jour', { type: 'success' });
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    } finally {
      setSaving((p) => ({ ...p, [specialty.id]: false }));
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  async function handleDelete(specialty: SectorSpecialty) {
    setDeleteTarget(null);
    setDeleting((p) => ({ ...p, [specialty.id]: true }));
    try {
      const res = await fetch(`${API_URL}/api/sector-specialties/${specialty.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      setSpecialties((prev) => prev.filter((s) => s.id !== specialty.id));
      notify('Spécialité supprimée', { type: 'success' });
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    } finally {
      setDeleting((p) => ({ ...p, [specialty.id]: false }));
    }
  }

  // ── Create ────────────────────────────────────────────────────────────────

  async function handleCreate() {
    if (!newLabel.trim() || !newValue.trim() || !newSectorId) {
      notify('Veuillez remplir tous les champs requis', { type: 'warning' });
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/sector-specialties`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          label: newLabel.trim(),
          value: newValue.trim().toUpperCase().replace(/\s+/g, '_'),
          sectorId: newSectorId,
          emoji: newEmoji.trim() || undefined,
          displayOrder: newOrder ? parseInt(newOrder, 10) : 0,
        }),
      });
      const created = await res.json();
      if (!res.ok) throw new Error(created.message || `HTTP ${res.status}`);

      // Fetch the complete object with sector relation
      await fetchData();

      setNewSectorId('');
      setNewLabel('');
      setNewValue('');
      setNewEmoji('');
      setNewOrder('');
      notify('Spécialité créée', { type: 'success' });
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    } finally {
      setCreating(false);
    }
  }

  // ── Filtering ──────────────────────────────────────────────────────────────

  const filteredSpecialties = selectedSectorFilter
    ? specialties.filter((s) => s.sectorId === selectedSectorFilter)
    : specialties;

  // Group by sector for display
  const groupedSpecialties = filteredSpecialties.reduce((acc, specialty) => {
    const sectorLabel = specialty.sector.label;
    if (!acc[sectorLabel]) acc[sectorLabel] = [];
    acc[sectorLabel].push(specialty);
    return acc;
  }, {} as Record<string, SectorSpecialty[]>);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Spécialités" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WorkIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Spécialités par secteur
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {specialties.length} spécialité{specialties.length !== 1 ? 's' : ''} · Affichées dans le formulaire d'inscription
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Rafraîchir">
          <IconButton onClick={fetchData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Les spécialités permettent aux commerçants de préciser leur métier (ex: Menuisier, Plombier, etc.).
      </Alert>

      {/* Add form */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Ajouter une spécialité
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Secteur *</InputLabel>
            <Select
              value={newSectorId}
              onChange={(e) => setNewSectorId(e.target.value)}
              label="Secteur *"
            >
              {sectors
                .filter((s) => s.isActive)
                .map((sector) => (
                  <MenuItem key={sector.id} value={sector.id}>
                    {sector.emoji} {sector.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Emoji"
            value={newEmoji}
            onChange={(e) => setNewEmoji(e.target.value)}
            sx={{ width: 90 }}
            inputProps={{ style: { textAlign: 'center', fontSize: 20 } }}
            placeholder="🔧"
          />
          <TextField
            size="small"
            label="Label *"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            placeholder="Ex: Menuisier"
          />
          <TextField
            size="small"
            label="Code *"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            sx={{ flex: 1, minWidth: 150 }}
            placeholder="Ex: MENUISIER"
            helperText="Majuscules, sans espaces"
          />
          <TextField
            size="small"
            label="Ordre"
            value={newOrder}
            onChange={(e) => setNewOrder(e.target.value.replace(/\D/g, ''))}
            sx={{ width: 90 }}
            type="number"
            placeholder="0"
          />
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newLabel || !newValue || !newSectorId}
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
          >
            Ajouter
          </Button>
        </Box>
      </Paper>

      {/* Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Filtrer par secteur</InputLabel>
          <Select
            value={selectedSectorFilter}
            onChange={(e) => setSelectedSectorFilter(e.target.value)}
            label="Filtrer par secteur"
          >
            <MenuItem value="">
              <em>Tous les secteurs</em>
            </MenuItem>
            {sectors.map((sector) => (
              <MenuItem key={sector.id} value={sector.id}>
                {sector.emoji} {sector.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : Object.keys(groupedSpecialties).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Aucune spécialité trouvée</Typography>
        </Paper>
      ) : (
        Object.entries(groupedSpecialties).map(([sectorLabel, specs]) => (
          <Paper key={sectorLabel} sx={{ mb: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {specs[0]?.sector.emoji} {sectorLabel}
                <Chip label={specs.length} size="small" sx={{ ml: 1 }} />
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>Emoji</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell width={80}>Ordre</TableCell>
                    <TableCell width={80}>Actif</TableCell>
                    <TableCell width={120}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {specs.map((specialty) => (
                    <TableRow key={specialty.id}>
                      <TableCell>
                        <TextField
                          size="small"
                          value={getField(specialty, 'emoji') || ''}
                          onChange={(e) => setField(specialty.id, 'emoji', e.target.value)}
                          sx={{ width: 50 }}
                          inputProps={{ style: { textAlign: 'center', fontSize: 18 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={getField(specialty, 'label')}
                          onChange={(e) => setField(specialty.id, 'label', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={getField(specialty, 'value')}
                          onChange={(e) => setField(specialty.id, 'value', e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={getField(specialty, 'displayOrder')}
                          onChange={(e) =>
                            setField(specialty.id, 'displayOrder', parseInt(e.target.value, 10))
                          }
                          sx={{ width: 70 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={getField(specialty, 'isActive')}
                          onChange={(e) => setField(specialty.id, 'isActive', e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {isDirty(specialty.id) && (
                            <Tooltip title="Sauvegarder">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSave(specialty)}
                                disabled={saving[specialty.id]}
                              >
                                {saving[specialty.id] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SaveIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(specialty)}
                              disabled={deleting[specialty.id]}
                            >
                              {deleting[specialty.id] ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Supprimer la spécialité ?</DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir supprimer <strong>{deleteTarget?.label}</strong> ?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button onClick={() => deleteTarget && handleDelete(deleteTarget)} color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
